import React from "react";
import { 
  Box, 
  Typography, 
  Container, 
  Button, 
  Grid, 
  Paper,
  Avatar,
  useTheme,
  alpha
} from "@mui/material";
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import DiamondIcon from '@mui/icons-material/Diamond';
import HistoryIcon from '@mui/icons-material/History';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const About = () => {
  const theme = useTheme();

  const team = [
    { name: "James Harrington", role: "Founder & Chief Numismatist", avatar: "JH", expertise: "Ancient Coins" },
    { name: "Victoria Sterling", role: "Director of Authentication", avatar: "VS", expertise: "Gold Coins" },
    { name: "Marcus Chen", role: "Head of Acquisitions", avatar: "MC", expertise: "Medieval Era" },
  ];

  const milestones = [
    { year: "1985", event: "DiamondCoins founded in London" },
    { year: "1995", event: "Expanded to New York and Hong Kong" },
    { year: "2005", event: "Introduced blockchain authentication" },
    { year: "2015", event: "Launched private auction house" },
    { year: "2025", event: "40th anniversary of excellence" },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          height: '60vh',
          background: 'linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            background: 'radial-gradient(circle at 50% 50%, rgba(198, 167, 94, 0.2) 0%, transparent 50%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'primary.main',
                mb: 2,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
              }}
            >
              Since 1985
            </Typography>
            <Typography variant="h1" sx={{ mb: 3, maxWidth: '900px', color: '#2D2D2D' }}>
              A Legacy of Trust
              <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                in Numismatics
              </Box>
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, maxWidth: '700px', color: '#616161' }}>
              Four decades of connecting discerning collectors with the world's most 
              exceptional rare coins.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Mission Section */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Typography variant="h3" gutterBottom sx={{ color: '#2D2D2D' }}>
                Bridging Heritage and
                <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                  Modern Wealth
                </Box>
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: '#616161', fontSize: '1.2rem' }}>
                DiamondCoins was founded to bridge the gap between heritage and modern 
                wealth preservation. We specialize in sourcing rare and investment-grade 
                coins from reputable mints, estates, and private collections worldwide.
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: '#616161', fontSize: '1.2rem' }}>
                Our experts carefully verify authenticity, grading, and provenance before 
                listing any piece. Every coin is insured for secure transactions.
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                component={Link}
                to="/collection"
                endIcon={<ArrowForwardIcon />}
              >
                Explore Collection
              </Button>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {[
                { number: "40+", label: "Years of Excellence" },
                { number: "15k+", label: "Coins Sold" },
                { number: "50+", label: "Countries Served" },
                { number: "100%", label: "Authenticated" },
              ].map((stat, index) => (
                <Grid item xs={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Paper
                      sx={{
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid',
                        borderColor: alpha('#9E9E9E', 0.15),
                        boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                      }}
                    >
                      <Typography variant="h3" color="primary.main" gutterBottom>
                        {stat.number}
                      </Typography>
                      <Typography variant="body2" color="#616161">
                        {stat.label}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>

      {/* Timeline Section */}
      <Box sx={{ backgroundColor: '#F5F5F5', py: 12 }}>
        <Container maxWidth="md">
          <Typography variant="h3" align="center" gutterBottom sx={{ color: '#2D2D2D' }}>
            Our Journey
          </Typography>
          <Typography variant="body1" align="center" color="#616161" sx={{ mb: 8 }}>
            Four decades of milestones and achievements
          </Typography>

          <Timeline position="alternate">
            {milestones.map((item, index) => (
              <TimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot sx={{ backgroundColor: 'primary.main' }}>
                    <HistoryIcon sx={{ color: '#FFFFFF' }} />
                  </TimelineDot>
                  {index < milestones.length - 1 && <TimelineConnector sx={{ backgroundColor: alpha('#9E9E9E', 0.3) }} />}
                </TimelineSeparator>
                <TimelineContent>
                  <Paper sx={{ p: 3, backgroundColor: '#FFFFFF', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                    <Typography variant="h5" color="primary.main" gutterBottom>
                      {item.year}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#2D2D2D' }}>{item.event}</Typography>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Container>
      </Box>

      {/* Team Section */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ color: '#2D2D2D' }}>
          Meet Our Experts
        </Typography>
        <Typography variant="body1" align="center" color="#616161" sx={{ mb: 8 }}>
          Led by world-renowned numismatists and authentication specialists
        </Typography>

        <Grid container spacing={4}>
          {team.map((member, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#9E9E9E', 0.15),
                    boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 3,
                      backgroundColor: 'primary.main',
                      color: '#FFFFFF',
                      fontSize: '2.5rem',
                      fontWeight: 600,
                    }}
                  >
                    {member.avatar}
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ color: '#2D2D2D' }}>
                    {member.name}
                  </Typography>
                  <Typography variant="body2" color="primary.main" gutterBottom>
                    {member.role}
                  </Typography>
                  <Typography variant="body2" color="#616161">
                    Expertise: {member.expertise}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        py: 12,
        background: 'linear-gradient(135deg, #2D2D2D 0%, #4A4A4A 100%)',
      }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ color: '#FFFFFF' }}>
            Begin Your Collection
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: alpha('#FFFFFF', 0.8) }}>
            Join the world's most discerning collectors
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/collection"
            sx={{
              backgroundColor: '#C6A75E',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#B08C4A',
              },
              px: 6,
              py: 2,
            }}
          >
            View Collection
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default About;